import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Printable QR code card — points at the public highlights page or the
// photographer's own website.
export default function QrCodeCard({ slug }) {
  const [target, setTarget] = useState("highlights");
  const [website, setWebsite] = useState("");
  const [qrUrl, setQrUrl] = useState(null);

  useEffect(() => {
    api.get("/photographer/me").then((r) => setWebsite(r.data.website || "")).catch(() => {});
  }, []);

  useEffect(() => {
    let current = null;
    (async () => {
      try {
        const resp = await api.get(`/photographer/me/qr?target=${target}`, { responseType: "blob" });
        current = URL.createObjectURL(resp.data);
        setQrUrl(current);
      } catch (e) {
        setQrUrl(null);
        toast.error(formatApiError(e));
      }
    })();
    return () => { if (current) URL.revokeObjectURL(current); };
  }, [target]);

  const download = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `photomoto-qr-${target}.png`;
    a.click();
  };

  const destination = target === "website"
    ? (website || "your website")
    : `photomoto.co.uk/p/${slug}`;

  return (
    <div className="border border-white/10 p-6 bg-[#0a0a0a]" data-testid="qr-card">
      <h3 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-3">Your QR code</h3>
      <p className="text-sm text-white/60 font-condensed mb-4">
        Print it on cards or banners at race meetings — riders scan it and land straight on {target === "website" ? "your website" : "your highlights page"}.
      </p>

      <div className="flex gap-4 mb-4 text-xs uppercase tracking-[0.2em]">
        <label className="flex items-center gap-2 text-white/70 cursor-pointer">
          <input type="radio" name="qr-target" checked={target === "highlights"} onChange={() => setTarget("highlights")} className="accent-[var(--pm-orange)]" data-testid="qr-target-highlights" />
          Highlights page
        </label>
        <label className={`flex items-center gap-2 cursor-pointer ${website ? "text-white/70" : "text-white/25"}`}>
          <input type="radio" name="qr-target" checked={target === "website"} onChange={() => setTarget("website")} disabled={!website} className="accent-[var(--pm-orange)]" data-testid="qr-target-website" />
          My website
        </label>
      </div>

      <div className="flex items-center gap-5 flex-wrap">
        <div className="bg-white p-2 inline-block">
          {qrUrl ? (
            <img src={qrUrl} alt={`QR code for ${destination}`} className="w-36 h-36" data-testid="qr-image" />
          ) : (
            <div className="w-36 h-36 flex items-center justify-center text-black text-xs">Loading…</div>
          )}
        </div>
        <div className="space-y-3 min-w-0">
          <p className="text-xs text-white/50 font-condensed break-all">→ {destination}</p>
          <button
            onClick={download}
            disabled={!qrUrl}
            className="border border-white/20 hover:border-white/60 px-5 py-2 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
            data-testid="qr-download"
          >
            Download PNG
          </button>
        </div>
      </div>
      {!website && (
        <p className="text-[11px] text-white/30 font-condensed mt-3">Add a website in My Settings to unlock the website QR option.</p>
      )}
    </div>
  );
}
